import classNames from 'classnames';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';

import { WIZE_SUBSCRIPTION_NAME } from 'my-core/constants';
import { hasContent, textToDraftJs } from 'my-core/editor-utils';
import { useSiteTourStep } from 'my-core/hooks';
import { getIsPurchased } from 'my-core/online-course-user-utils';
import { ACCESS_LEVELS, isSubscriptionOffering } from 'my-core/online-course-utils';
import { subscriptionLandingPath, tutorSearchPath } from 'my-core/routes';
import { SITE_TOURS } from 'my-core/site-tour';
import {
  getHasPaymentIssue,
  getIsActiveOrTrialing,
} from /*, SUBSCRIPTION_QUESTION_QUOTA */ 'my-core/subscription-utils';
import { isMobileDevice } from 'my-core/user-agent-utils';
import { pluralize } from 'my-utils';

import EditorMobile from 'my-components/editor/EditorMobile';
import { EDITOR_PLUGINS } from 'my-components/OnlineCourseQuestion';
import Editor from 'my-editor';
import MuiAvatar from 'my-elements/MuiAvatar';

import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';

const rootCss = ({ breakpoints, palette, shape: { borderRadius }, spacing }) => ({
  '.OCQF-actions': {
    textAlign: 'center',
    marginTop: spacing(2),
    '& > *': { margin: spacing(0, 0.5) },
  },
  '.OCQF-row': {
    display: 'flex',
    alignItems: 'center',
    '& + .OCQF-row': { marginTop: spacing(1) },
  },
  '.OCQF-avatar': {
    width: 40,
    height: 40,
  },
  '.OCQF-editor': {
    flex: 1,
    marginLeft: spacing(1),
    padding: spacing(1),
    backgroundColor: palette.background,
    border: '1px solid ' + palette.grey[300],
    borderRadius,
  },
  '.OCQF-editor_mobile': {
    padding: spacing(0.5, 1),
  },
  '.OCQF-spacer': { flex: 1 },
  '.OCQF-buttons': {
    '& > button': { marginLeft: spacing(1) },
  },
  '.OCQF-aiNote': { display: 'block', marginTop: spacing(1) },

  [breakpoints.only('xs')]: {
    '.OCQF-avatar': {
      width: 32,
      height: 32,
    },
    '.OCQF-editor': {
      minHeight: 32,
    },
  },
});

export default function OnlineCourseQuestionForm({
  currentUser,
  enableTour,
  onlineCourse,
  onlineCourseUser,
  onPurchase,
  onSave,
  subscription,
  userCourse,
}) {
  const isMobile = isMobileDevice();

  const [draft, setDraft] = useState({ content: null, anonymous: false });

  const { anonymous, content } = draft;

  const updateContent = useCallback(content => setDraft(d => ({ ...d, content })), []);

  const remainingQuestions =
    (
      onlineCourse?.user_access_level >= ACCESS_LEVELS.FULL_VIEW ||
      onlineCourseUser?.remaining_questions < 0 ||
      userCourse?.remaining_questions < 0 ||
      (onlineCourse && isSubscriptionOffering(onlineCourse) && getIsActiveOrTrialing(subscription))
    ) ?
      -1 // unlimited
      // : ((isSubOffering && subscription?.remaining_questions) || 0) +
    : (onlineCourseUser?.remaining_questions || 0) + (userCourse?.remaining_questions || 0);
  const totalQuestions = remainingQuestions < 0 ? 0 : onlineCourseUser?.total_questions || 0; // +
  // (isSubOffering && subscription?.status === 'active' ? SUBSCRIPTION_QUESTION_QUOTA : 0);
  const aiTutorReplies =
    !!onlineCourse?.in_course_settings && !onlineCourse.in_course_settings.ai_tutor_replies_disabled;

  const rootRef = useRef(null);
  const { id: tourId, steps } = SITE_TOURS.ONLINE_COURSE_QUESTIONS;
  const tourStep = useMemo(
    () => ({
      ...(aiTutorReplies ? steps.aiTutorQuestions : steps.questions),
      // The course page scrolls inside its columns, out of reach of the tour's own window scrolling: skip that
      // (`fixed`), so the spotlight doesn't wait out a scroll that moves nothing, and scroll the box in here.
      fixed: true,
      onBefore: cb => {
        rootRef.current?.scrollIntoView({ block: 'nearest' });
        cb();
      },
    }),
    [aiTutorReplies, steps.aiTutorQuestions, steps.questions],
  );
  const tourStepId = useSiteTourStep({ disabled: !enableTour || !remainingQuestions, step: tourStep, tourId });

  if (!remainingQuestions) {
    const [text, btns] = getNoQuestionsContent();

    return (
      <div css={rootCss}>
        <Typography align="center" gutterBottom variant="h5">
          Ask a Question
        </Typography>
        <Typography align="center" sx={{ color: 'text.secondary' }} variant="body1">
          {text}
        </Typography>
        <div className="OCQF-actions">
          {btns?.map((btnProps, idx) => (
            <Button key={idx} {...btnProps} />
          ))}
        </div>
      </div>
    );
  }

  const isEmpty = typeof content === 'object' ? !hasContent(content) : !content;

  return (
    <div css={rootCss} id={tourStepId} ref={rootRef}>
      <Typography gutterBottom variant="subtitle1">
        Ask a Question
      </Typography>
      {remainingQuestions > 0 && (
        <Typography gutterBottom sx={{ display: 'block', color: 'text.secondary' }} variant="overline">
          {`${pluralize(remainingQuestions, 'Question')} Remaining`}
        </Typography>
      )}
      {/*totalQuestions > 0 && (
        <Typography color="textSecondary" display="block" gutterBottom variant="overline">
          {`${totalQuestions - remainingQuestions}/${totalQuestions} Questions Asked`}
        </Typography>
      )*/}
      {renderForm()}
    </div>
  );

  function getNoQuestionsContent() {
    /* eslint-disable @eslint-react/no-missing-key */
    if (onlineCourse && isSubscriptionOffering(onlineCourse)) {
      // if (subscription?.status === 'active') {
      //   return [
      //     `Your questions will reset to ${SUBSCRIPTION_QUESTION_QUOTA} on ${formatDate(
      //       subscription.end_of_cycle,
      //       'upcomingDate'
      //     )}`
      //   ];
      // }
      if (getHasPaymentIssue(subscription)) {
        return [
          'We had some issues charging your card in your last invoice. Please update your card details',
          [{ onClick: onPurchase, children: 'Update Card' }],
        ];
      }

      return [
        // `Subscribe to ${WIZE_SUBSCRIPTION_NAME} and ask ${SUBSCRIPTION_QUESTION_QUOTA} questions per month`,
        `Upgrade to ${WIZE_SUBSCRIPTION_NAME} and get unlimited questions answered.`,
        [
          { component: Link, nativeButton: false, to: subscriptionLandingPath(), children: 'Learn More' },
          { onClick: onPurchase, children: 'Subscribe', variant: 'outlined' },
        ],
      ];
    }
    if (totalQuestions > 0) {
      return [
        <>
          You have used up all the questions that come with this course. If you need extra help you can try out our{' '}
          <MuiLink component={Link} to={tutorSearchPath()}>
            online tutoring service.{' '}
          </MuiLink>
        </>,
      ];
    }
    if (onlineCourse?.default_user_question_count && onlineCourse.price && !getIsPurchased(onlineCourseUser)) {
      return [
        `Upgrade for full access to the course videos, solutions and ${onlineCourse?.default_user_question_count} questions`,
        [{ onClick: onPurchase, children: 'Upgrade Now' }],
      ];
    }
    return ['You cannot ask questions in this course'];
    /* eslint-enable @eslint-react/no-missing-key */
  }

  function renderForm() {
    return (
      <>
        <div className="OCQF-row">
          <MuiAvatar anonymous={anonymous} className="OCQF-avatar" src="thumb" user={currentUser} />
          {isMobile ?
            <EditorMobile
              className={classNames('OCQF-editor', 'OCQF-editor_mobile')}
              onChange={updateContent}
              placeholder="Your question here..."
              value={content}
            />
          : <Editor
              className="OCQF-editor"
              onChange={updateContent}
              placeholder="Your question here..."
              plugins={EDITOR_PLUGINS}
              value={content}
            />
          }
        </div>
        <div className="OCQF-row">
          <FormControlLabel
            control={
              <Checkbox
                checked={anonymous}
                onChange={e => setDraft({ ...draft, anonymous: e.target.checked })}
                size="small"
              />
            }
            label="Ask Anonymously"
            slotProps={{ typography: { variant: 'caption' } }}
          />
          <div className="OCQF-spacer" />
          <div className="OCQF-buttons">
            <Button
              disabled={isEmpty}
              onClick={() => setDraft({ ...draft, content: null })}
              size="small"
              variant="text"
            >
              Clear
            </Button>
            <Button
              disabled={isEmpty}
              onClick={() =>
                onSave({
                  ...draft,
                  content: typeof content === 'object' ? content : textToDraftJs(content),
                })
              }
              size="small"
              variant="contained"
            >
              Ask
            </Button>
          </div>
        </div>
        {aiTutorReplies && (
          <Typography
            className="OCQF-aiNote"
            sx={{
              color: 'text.secondary',
            }}
            variant="caption"
          >
            The Wize AI Tutor answers right away, and you can send it to your Wizeprep Prof anytime.
          </Typography>
        )}
      </>
    );
  }
}
